import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@/shared/ui';
import { usePermission } from '@/shared/hooks/usePermission';
import type { ContactLog } from './api/calls.api';
import { callsAPI } from './api/calls.api';
import { formatPhone } from '@/shared/utils/validation';

interface IncomingCallDialogProps {
  incoming: ContactLog | null;
  onClose: () => void;
}

const IncomingCallDialog: React.FC<IncomingCallDialogProps> = ({ incoming, onClose }) => {
  const { t } = useTranslation('calls');
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  if (!incoming || incoming.call?.status !== 'ringing' || incoming.call.direction !== 'inbound') {
    return null;
  }

  const phone = incoming.call.phoneNumber;
  const candidates = incoming.candidates ?? [];
  const subject = incoming.subject;

  const openPath = (kind: 'lead' | 'patient', id: string) =>
    kind === 'patient' ? `/patients/${id}` : `/leads/${id}`;

  const openRecord = (kind: 'lead' | 'patient', id: string) => {
    navigate(openPath(kind, id));
    onClose();
  };

  const linkThenOpen = (kind: 'lead' | 'patient', id: string) => {
    const go = () => openRecord(kind, id);
    if (!hasPermission('calls:read')) {
      go();
      return;
    }
    void callsAPI
      .linkContact(incoming.id, kind === 'lead' ? { leadId: id } : { patientId: id })
      .finally(go);
  };

  const createLead = () => {
    navigate(phone ? `/leads/new?phone=${encodeURIComponent(phone)}` : '/leads/new');
    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('incoming.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Typography variant="h6">{phone ? formatPhone(phone) : t('incoming.unknown')}</Typography>
          {subject ? (
            <Typography variant="body2" color="text.secondary">
              {subject.name}
            </Typography>
          ) : candidates.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('incoming.multiple')}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('incoming.unknown')}
            </Typography>
          )}
          {candidates.map((row) => (
            <Button key={`${row.kind}:${row.id}`} onClick={() => linkThenOpen(row.kind, row.id)}>
              {row.name}
            </Button>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        {subject ? (
          <Button intent="primary" onClick={() => openRecord(subject.kind, subject.id)}>
            {t('actions.openRecord')}
          </Button>
        ) : null}
        {!subject ? (
          <Button intent="primary" onClick={createLead}>
            {t('actions.createLead')}
          </Button>
        ) : null}
        <Button onClick={onClose}>{t('actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncomingCallDialog;
